import React from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import PWAInstallPrompt from "./components/PWAInstallPrompt";

import { AuthProvider } from "./lib/auth";
import { CurrencyProvider } from "./lib/currency";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import FloatingFacebookPill from "./components/FloatingFacebookPill";
import AppErrorBoundary from "./components/AppErrorBoundary";

import Landing from "./pages/Landing";
import Services from "./pages/Services";
import Pricing from "./pages/Pricing";
import Domains from "./pages/Domains";
import AIBuilder from "./pages/AIBuilder";
import AIAppBuilder from "./pages/AIAppBuilder";
import AITools from "./pages/AITools";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import VerifyEmail from "./pages/VerifyEmail";
import { StaticUpiAmount, StaticUpiCheckout, StaticUpiPay, StaticUpiReceipt } from "./pages/StaticUpi";
import { SM888Pay, SM888Status } from "./pages/SM888";
import Developers from "./pages/Developers";
import Checkout from "./pages/Checkout";
import AdminDashboard from "./pages/AdminDashboard";
import Contact from "./pages/Contact";
import Portfolio from "./pages/Portfolio";
import PortfolioDetail from "./pages/PortfolioDetail";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import PayInvoice from "./pages/PayInvoice";
import { PrivacyPolicy, TermsOfService, RefundPolicy } from "./pages/LegalPages";

import DashboardLayout from "./pages/dashboard/DashboardLayout";
import DashboardHome from "./pages/dashboard/DashboardHome";
import Projects from "./pages/dashboard/Projects";
import Orders from "./pages/dashboard/Orders";
import AIJobs from "./pages/dashboard/AIJobs";
import Tickets from "./pages/dashboard/Tickets";
import Subscriptions from "./pages/dashboard/Subscriptions";
import Account from "./pages/dashboard/Account";
import AICredits from "./pages/dashboard/AICredits";
import AIImages from "./pages/dashboard/AIImages";
import MySites from "./pages/dashboard/MySites";
import OrderKyc from "./pages/dashboard/OrderKyc";
import { PayGlocalSuccess, PayGlocalFailed } from "./pages/PayGlocalReturn";
import { PGAdmin, PGCustomerPay, PGStatus } from "./pages/PaymentLinks";
import SEOAnalyzer from "./pages/dashboard/SEOAnalyzer";
import KeywordResearch from "./pages/dashboard/KeywordResearch";
import SEOAssistant from "./pages/dashboard/SEOAssistant";
import SEOSuite from "./pages/dashboard/SEOSuite";
import AIAssistance from "./pages/dashboard/AIAssistance";
import SitePreview from "./pages/SitePreview";

import "./App.css";

/** Shell with public Navbar + Footer — used for marketing/public pages only. */
const Shell = ({ children }) => (
  <div className="min-h-screen flex flex-col" style={{background: 'var(--bg-page)'}}>
    <Navbar />
    <main className="flex-1">{children}</main>
    <Footer />
    <FloatingFacebookPill />
  </div>
);

/** Portal shell — no public navbar/footer. Used for /admin and /dashboard. */
const Portal = ({ children }) => (
  <div className="min-h-screen" style={{background: 'var(--bg-page)'}}>
    {children}
  </div>
);

function App() {
  return (
    <AppErrorBoundary>
      <CurrencyProvider>
        <AuthProvider>
          <BrowserRouter>
          <Routes>
            {/* Public site — wrapped in Shell (public Navbar + Footer) */}
            <Route path="/" element={<Shell><Landing /></Shell>} />
            <Route path="/services" element={<Shell><Services /></Shell>} />
            <Route path="/pricing" element={<Shell><Pricing /></Shell>} />
            <Route path="/domains" element={<Shell><Domains /></Shell>} />
            <Route path="/ai-builder" element={<Shell><AIBuilder /></Shell>} />
            <Route path="/ai-app-builder" element={<Shell><AIAppBuilder /></Shell>} />
            <Route path="/ai-tools" element={<Shell><AITools /></Shell>} />
            <Route path="/login" element={<Shell><Login /></Shell>} />
            <Route path="/signup" element={<Shell><Signup /></Shell>} />
            <Route path="/verify-email" element={<Shell><VerifyEmail /></Shell>} />
            {/* Static UPI mobile-only checkout — no <Shell> so it takes over
                the full viewport with the branded card layout. */}
            <Route path="/static-qr" element={<StaticUpiAmount/>} />
            <Route path="/SM888" element={<SM888Pay/>} />
            <Route path="/sm888" element={<SM888Pay/>} />
            <Route path="/SM888/status/:orderId" element={<SM888Status/>} />
            <Route path="/sm888/status/:orderId" element={<SM888Status/>} />
            <Route path="/static-qr/checkout" element={<StaticUpiCheckout/>} />
            <Route path="/static-qr/pay/:paymentId" element={<StaticUpiPay/>} />
            <Route path="/static-qr/receipt/:paymentId" element={<StaticUpiReceipt/>} />
            <Route path="/checkout" element={<Shell><Checkout /></Shell>} />
            <Route path="/checkout/payglocal/success" element={<Shell><PayGlocalSuccess /></Shell>} />
            <Route path="/checkout/payglocal/failed" element={<Shell><PayGlocalFailed /></Shell>} />
            <Route path="/contact" element={<Shell><Contact /></Shell>} />
            <Route path="/developers" element={<Shell><Developers /></Shell>} />
            <Route path="/portfolio" element={<Shell><Portfolio /></Shell>} />
            <Route path="/portfolio/:slug" element={<Shell><PortfolioDetail /></Shell>} />
            <Route path="/blog" element={<Shell><Blog /></Shell>} />
            <Route path="/blog/:slug" element={<Shell><BlogPost /></Shell>} />
            <Route path="/site/:slug" element={<Shell><SitePreview /></Shell>} />
            <Route path="/preview/:slug" element={<Shell><SitePreview /></Shell>} />
            <Route path="/privacy" element={<Shell><PrivacyPolicy /></Shell>} />
            <Route path="/terms" element={<Shell><TermsOfService /></Shell>} />
            <Route path="/refund-policy" element={<Shell><RefundPolicy /></Shell>} />

            {/* Customer Portal — standalone, no public navbar/footer */}
            <Route path="/dashboard" element={<Portal><DashboardLayout /></Portal>}>
              <Route index element={<DashboardHome />} />
              <Route path="projects" element={<Projects />} />
              <Route path="assistance" element={<AIAssistance />} />
              <Route path="orders" element={<Orders />} />
              <Route path="orders/:orderId/kyc" element={<OrderKyc />} />
              <Route path="subscriptions" element={<Subscriptions />} />
              <Route path="credits" element={<AICredits />} />
              <Route path="images" element={<AIImages />} />
              <Route path="sites" element={<MySites />} />
              <Route path="seo/analyzer" element={<SEOAnalyzer />} />
              <Route path="seo/keywords" element={<KeywordResearch />} />
              <Route path="seo/assistant" element={<SEOAssistant />} />
              <Route path="seo/suite" element={<SEOSuite />} />
              <Route path="ai" element={<AIJobs />} />
              <Route path="tickets" element={<Tickets />} />
              <Route path="account" element={<Account />} />
            </Route>

            {/* Admin Portal — standalone */}
            <Route path="/admin" element={<Portal><AdminDashboard /></Portal>} />

            {/* Public invoice payment (no Shell, full-screen branded page) */}
            <Route path="/pay/:invoiceId" element={<PayInvoice />} />

            {/* Payment Link Generator (admin /PG + customer /pl/:token) */}
            <Route path="/PG" element={<PGAdmin />} />
            <Route path="/pg" element={<PGAdmin />} />
            <Route path="/pl/:token" element={<PGCustomerPay />} />
            <Route path="/pl/:token/status" element={<PGStatus />} />

            {/* 404 */}
            <Route path="*" element={
              <Shell>
                <div className="section text-center">
                  <h1 className="text-4xl font-black text-white" style={{fontFamily:'Outfit'}}>404 — Page not found</h1>
                  <a href="/" className="btn-primary mt-5 inline-flex">Back to home</a>
                </div>
              </Shell>
            }/>
          </Routes>
          <Toaster theme="dark" position="top-right" richColors toastOptions={{ style: { marginTop: "60px" } }} />
          <PWAInstallPrompt />
        </BrowserRouter>
      </AuthProvider>
    </CurrencyProvider>
    </AppErrorBoundary>
  );
}

export default App;
